/**
 * Reusable Create Task Modal Component
 * Can be used in both Tasks page and Board page
 */
import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createTask, fetchTasks } from '@/store/taskSlice';
import { fetchProjects } from '@/store/projectSlice';
import { Modal } from '@/components/Modal';
import { mastersAPI, TaskType, Priority, WorkflowStatus, IworkFlowListDetails } from '@/api/mastersApi';
import toast from 'react-hot-toast';
import { RichTextEditor, isRichTextEmpty } from '@/components/RichTextEditor/RichTextEditor';
import { ensureHtml } from '@/utils/richText';
import { projectMemberAPI } from '@/api/projectMemberAPI';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultProjectId?: string; // If provided, project field will be pre-filled and disabled
  onSuccess?: () => void; // Callback when task is created successfully
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  defaultProjectId,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { projects } = useAppSelector((state) => state.projects);
  const { user: currentUser } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState<any>({
    title: '',
    description: '',
    task_type_id: '',
    priority_id: '',
    project_id: defaultProjectId || '',
    workflow_status_id: '',
    story_points: undefined,
  });

  // Master data states
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<IworkFlowListDetails[]>([]);
  
  // Viewer-only status per project, derived from project.my_role from the
  // already-loaded projects list. Replaces the previous N×checkMembership
  // loop that fired on every Tasks-page mount even with the modal closed.
  const projectViewerStatus = useMemo(() => {
    const isAdminLike = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.role?.toLowerCase() === 'super_admin';
    const map: Record<string, boolean> = {};
    if (!currentUser?.id) return map;
    projects.forEach((p: any) => {
      if (!p?.id) return;
      const role = (p?.my_role || '').toLowerCase();
      map[p.id] = !isAdminLike && role === 'viewer';
    });
    return map;
  }, [projects, currentUser?.id, currentUser?.role]);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load projects only when the modal opens. Tasks/Dashboard pages already
  // fetched projects, so this is just a refresh and only matters if the modal
  // is actually about to be shown.
  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchProjects({ is_active: true, organization_id: currentUser?.organization_id }));
  }, [dispatch, isOpen, currentUser?.organization_id]);

  // Load master data only when the modal is opened.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const types = await mastersAPI.getTaskTypes();
        setTaskTypes(types.filter(t => t.is_active !== false));
      } catch { }
      try {
        const prios = await mastersAPI.getPriorities();
        setPriorities(prios.filter(p => p.is_active !== false).sort((a, b) => a.order - b.order));
      } catch { }
    })();
  }, [isOpen]);

  // Load workflow statuses when project changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!formData.project_id) {
        setWorkflowStatuses([]);
        return;
      }
      try {
        const response = await mastersAPI.getWorkflowStatusesList(formData.project_id);
        if (mounted && response.statuses && response.statuses.length) {
          const sorted = response.statuses.sort((a, b) => a.order - b.order);
          setWorkflowStatuses(sorted);
        } else if (mounted) {
          setWorkflowStatuses([]);
        }
      } catch (error: any) {
        if (mounted) {
          toast.error(error?.message || 'Failed to load workflow statuses');
          setWorkflowStatuses([]);
        }
      }
    })();
    return () => { mounted = false; };
  }, [formData.project_id]);

  // Set default project when defaultProjectId changes
  useEffect(() => {
    if (defaultProjectId) {
      setFormData((prev: any) => ({ ...prev, project_id: defaultProjectId }));
    }
  }, [defaultProjectId]);

  // Set default task type when modal opens or masters are loaded
  useEffect(() => {
    if (isOpen && taskTypes.length > 0) {
      setFormData((prev: any) => {
        if (!prev.task_type_id || !taskTypes.some(t => t.id === prev.task_type_id)) {
          const defaultType = taskTypes.find(t => t.name.toLowerCase() === 'task') || taskTypes[0];
          if (defaultType) {
            return { ...prev, task_type_id: defaultType.id };
          }
        }
        return prev;
      });
    }
  }, [isOpen, taskTypes]);

  // Set default priority when modal opens or masters are loaded
  useEffect(() => {
    if (isOpen && priorities.length > 0) {
      setFormData((prev: any) => {
        if (prev.project_id && (!prev.priority_id || !priorities.some(p => p.id === prev.priority_id))) {
          const defaultPrio = priorities.find(p => p.key === 'medium') || priorities[0];
          if (defaultPrio) {
            return { ...prev, priority_id: defaultPrio.id };
          }
        }
        return prev;
      });
    }
  }, [isOpen, priorities, formData.project_id]);

  // Set default workflow status when modal opens or statuses are loaded
  useEffect(() => {
    if (isOpen && workflowStatuses.length > 0) {
      setFormData((prev: any) => {
        const currentStatusValid = prev.workflow_status_id && workflowStatuses.some(s => s.id === prev.workflow_status_id);
        if (!currentStatusValid) {
          const defaultStatus = workflowStatuses.find(s => s.is_first || s.is_initial) || workflowStatuses[0];
          if (defaultStatus) {
            return { ...prev, workflow_status_id: defaultStatus.id };
          }
        }
        return prev;
      });
    }
  }, [isOpen, workflowStatuses]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      const defaultType = taskTypes.find(t => t.name.toLowerCase() === 'task');
      const defaultPrio = priorities.find(p => p.key === 'medium');
      setFormData({
        title: '',
        description: '',
        task_type_id: defaultType?.id || '',
        priority_id: defaultPrio?.id || '',
        project_id: defaultProjectId || '',
        workflow_status_id: '',
        story_points: undefined,
      });
    }
  }, [isOpen, defaultProjectId, taskTypes, priorities]);

  useEffect(() => {
    const fetchMembers = async () => {

      if (!formData.project_id) {
        setProjectMembers([]);
        return;
      }
      
      setIsLoadingMembers(true);
      try {
        const response = await projectMemberAPI.getProjectMembers(formData.project_id);
        const dataToMap = Array.isArray(response) ? response :[];
      
      const mappedData = dataToMap?.map((item:any) => ({
        label: item.username, 
        value: item.user_id
      }));
      setProjectMembers(mappedData);
      } catch (error) {
        toast.error("Could not load members for this project");
      } finally {
        setIsLoadingMembers(false);
      }
    };
  
    fetchMembers();
  }, [formData.project_id]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim().length < 3) {
      toast.error('Task title must be at least 3 characters long');
      return;
    }

    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }

    if(!formData.assignee_id){
      toast.error("Please select the Assignee")
      return;
    }

    setIsSubmitting(true);
    try {
      const taskData: any = {
        title: formData.title.trim(),
        description: formData.description || undefined,
        project_id: formData.project_id,
        labels: undefined,
        assigned_to:formData.assignee_id
      };

      // Add master IDs if selected
      if (formData.task_type_id) {
        taskData.task_type_id = formData.task_type_id;
      }
      if (formData.priority_id) {
        taskData.priority_id = formData.priority_id;
      }
      if (formData.story_points !== undefined && formData.story_points !== null && formData.story_points !== '') {
        taskData.story_points = parseInt(String(formData.story_points));
      }
      if (formData.workflow_status_id) {
        taskData.workflow_status_id = formData.workflow_status_id;
      }

      // Set default workflow_status_id to "todo" (first initial status or status named "todo"/"to do")
      if (!taskData.workflow_status_id) {
        const todoStatus = workflowStatuses.find(s =>
           s.is_first|| s.is_initial
        );
        if (todoStatus) {
          taskData.workflow_status_id = todoStatus.id;
        } else if (workflowStatuses.length > 0) {
          // Fallback to first status if no "todo" status found
          taskData.workflow_status_id = workflowStatuses[0].id;
        }
      }

      await dispatch(createTask(taskData)).unwrap();
      
      // Refresh tasks after creation
      if (formData.project_id) {
        await dispatch(fetchTasks({ project_id: formData.project_id }));
      } else {
        await dispatch(fetchTasks({}));
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      // Error toast is handled by the createTask thunk
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Task"
      size="lg"
    >
      <form onSubmit={handleCreateTask} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="input"
            minLength={3}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <RichTextEditor
            value={ensureHtml(formData.description)}
            onChange={(html) =>
              setFormData((prev: any) => ({
                ...prev,
                description: isRichTextEmpty(html) ? '' : html
              }))
            }
            placeholder="Describe the task, add links, checklists, etc..."
            minHeight={150}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project
            </label>
            <select
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              className="input"
              required
              disabled={!!defaultProjectId}
            >
              <option value="">Select project</option>
              {projects
                .filter((project) => {
                  // Filter out projects where user is a viewer
                  if (!project.id) return false;
                  return !(projectViewerStatus[project.id] ?? false);
                })
                .map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignee 
            </label>
            <select
              value={formData.assignee_id || ''}
              onChange={(e) => setFormData({ ...formData, assignee_id: e.target.value })}
              className="input"
              required
              disabled={!formData.project_id || isLoadingMembers}
            >
              <option value="">
                {!formData.project_id 
                  ? "Select a project first" 
                  : isLoadingMembers 
                    ? "Loading members..." 
                    : "Select Assignee"}
              </option>
              {projectMembers.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.task_type_id}
              onChange={(e) => setFormData({ ...formData, task_type_id: e.target.value })}
              className="input"
            >
              <option value="">Select Type</option>
              {taskTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority_id}
              onChange={(e) => setFormData({ ...formData, priority_id: e.target.value })}
              className="input"
            >
              <option value="">Select Priority</option>
              {priorities.map((priority) => (
                <option key={priority.id} value={priority.id}>{priority.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.workflow_status_id || ''}
              onChange={(e) => setFormData({ ...formData, workflow_status_id: e.target.value })}
              className="input"
              disabled={!formData.project_id}
            >
              <option value="">
                {!formData.project_id ? "Select a project first" : "Select Status"}
              </option>
              {workflowStatuses.map((status) => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Story Points
            </label>
            <input
              type="number"
              value={formData.story_points || ''}
              onChange={(e) => setFormData({ ...formData, story_points: e.target.value ? parseInt(e.target.value) : undefined })}
              className="input"
              min="0"
              max="100"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1 flex items-center justify-center gap-2">
            {isSubmitting && (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting ? 'Creating...' : 'Create Task'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

